import apiClient from './api.service';
import {
  ParticipantEngagementCycleResponse,
  ParticipantReferralListResponse,
  PublicEngagementWinnersResponse,
  PublicEngagementCurrentCycle,
  ParticipantTicketsResponse,
  ParticipantCycleHistorySummary,
  LotteryResultsResponse,
  RegulationResponse,
} from '../types/engagement.types';

interface WrappedResponse<T> {
  data: T;
}

class EngagementService {
  async getCurrentCycle(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ParticipantEngagementCycleResponse> {
    const response = await apiClient.get<WrappedResponse<ParticipantEngagementCycleResponse>>(
      '/api/participant/engagement/current-cycle',
      { params }
    );
    return response.data.data;
  }

  async listReferrals(params?: {
    page?: number;
    pageSize?: number;
  }): Promise<ParticipantReferralListResponse> {
    const response = await apiClient.get<WrappedResponse<ParticipantReferralListResponse>>(
      '/api/participant/engagement/referrals',
      { params }
    );
    return response.data.data;
  }

  async getPublicCurrentCycle(): Promise<PublicEngagementCurrentCycle | null> {
    const response = await apiClient.get<WrappedResponse<PublicEngagementCurrentCycle | null>>(
      '/api/public/engagement/current-cycle'
    );
    return response.data.data;
  }

  async getPublicWinners(cycleKey: string): Promise<PublicEngagementWinnersResponse> {
    const response = await apiClient.get<WrappedResponse<PublicEngagementWinnersResponse>>(
      `/api/public/engagement/cycles/${cycleKey}/winners`
    );
    return response.data.data;
  }

  // Feature 016: Lottery Tickets

  async getCurrentCycleTickets(): Promise<ParticipantTicketsResponse> {
    const response = await apiClient.get<WrappedResponse<ParticipantTicketsResponse>>(
      '/api/participant/engagement/current-cycle/tickets'
    );
    return response.data.data;
  }

  async getCycleHistory(params?: { page?: number; limit?: number }): Promise<{ cycles: ParticipantCycleHistorySummary[]; total: number }> {
    const response = await apiClient.get<WrappedResponse<ParticipantCycleHistorySummary[]>>(
      '/api/participant/engagement/cycles',
      { params }
    );

    const payload = response.data as unknown as {
      data?: unknown;
      pagination?: { total?: number };
    };

    const cycles = Array.isArray(payload?.data) ? payload.data as ParticipantCycleHistorySummary[] : [];
    const total = typeof payload?.pagination?.total === 'number'
      ? payload.pagination.total
      : cycles.length;

    return { cycles, total };
  }

  async getCycleTickets(cycleKey: string): Promise<ParticipantTicketsResponse> {
    const response = await apiClient.get<WrappedResponse<ParticipantTicketsResponse>>(
      `/api/participant/engagement/cycles/${cycleKey}/tickets`
    );
    return response.data.data;
  }

  async getLotteryResults(cycleKey: string): Promise<LotteryResultsResponse> {
    const response = await apiClient.get<WrappedResponse<LotteryResultsResponse>>(
      `/api/participant/engagement/cycles/${cycleKey}/lottery-results`
    );
    return response.data.data;
  }

  async getRegulation(): Promise<RegulationResponse> {
    const response = await apiClient.get<WrappedResponse<RegulationResponse>>(
      '/api/participant/engagement/regulation'
    );
    return response.data.data;
  }
}

export const engagementService = new EngagementService();
